/**
 * Achievement Manager - Tracks and displays in-game achievements
 */

import { AchievementToaster } from './AchievementToaster';

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string; // Emoji
  unlocked: boolean;
}

const ACHIEVEMENTS: Achievement[] = [
  { id: 'first_pop', name: 'First Pop', description: 'Pop your first bubble', icon: '🫧', unlocked: false },
  { id: 'combo_x4', name: 'Combo Master', description: 'Reach a x4 combo', icon: '🔥', unlocked: false },
  { id: 'combo_x8', name: 'Combo Legend', description: 'Reach a x8 combo', icon: '💥', unlocked: false },
  { id: 'perfect_clear', name: 'Perfect Clear', description: 'Clear a stage without losing a life', icon: '⭐', unlocked: false },
  { id: 'world1_complete', name: 'Reef Explorer', description: 'Complete World 1', icon: '🐠', unlocked: false },
  { id: 'world2_complete', name: 'Shore Guardian', description: 'Complete World 2', icon: '🏖️', unlocked: false },
  { id: 'world3_complete', name: 'Sea Captain', description: 'Complete World 3', icon: '🚢', unlocked: false },
  { id: 'boss_defeated', name: 'Ocean Savior', description: 'Defeat the Pollution Hydra', icon: '🏆', unlocked: false },
  { id: 'score_5000', name: 'Score Hunter', description: 'Score 5,000 points', icon: '💰', unlocked: false },
  { id: 'score_20000', name: 'Score King', description: 'Score 20,000 points', icon: '👑', unlocked: false },
  { id: 'shield_used', name: 'Protected', description: 'Use the Ocean Shield', icon: '🛡️', unlocked: false },
  { id: 'three_stars', name: 'Three Star General', description: 'Get 3 stars on any stage', icon: '🌟', unlocked: false },
];

export class AchievementManager {
  private static instance: AchievementManager;
  private achievements: Achievement[];
  private pendingNotifications: Achievement[] = [];
  private scene?: Phaser.Scene;

  private constructor() {
    // Load from localStorage
    const saved = this.loadFromStorage();
    this.achievements = ACHIEVEMENTS.map(a => ({
      ...a,
      unlocked: saved.includes(a.id),
    }));
  }

  static getInstance(): AchievementManager {
    if (!AchievementManager.instance) {
      AchievementManager.instance = new AchievementManager();
    }
    return AchievementManager.instance;
  }

  setScene(scene: Phaser.Scene): void {
    this.scene = scene;
  }

  /**
   * Unlock an achievement by ID. Shows notification if newly unlocked.
   */
  unlock(id: string): void {
    const achievement = this.achievements.find(a => a.id === id);
    if (!achievement || achievement.unlocked) return;

    achievement.unlocked = true;
    this.saveToStorage();
    this.pendingNotifications.push(achievement);
    this.showNextNotification();

    console.log(`🏆 Achievement unlocked: ${achievement.name}`);
  }

  /**
   * Check if an achievement is unlocked
   */
  isUnlocked(id: string): boolean {
    return this.achievements.find(a => a.id === id)?.unlocked || false;
  }

  /**
   * Get all achievements
   */
  getAll(): Achievement[] {
    return this.achievements;
  }

  private showNextNotification(): void {
    if (!this.scene || this.pendingNotifications.length === 0) return;
    const achievement = this.pendingNotifications.shift()!;
    // Route through the new AchievementToaster (Clash of Clans-styled,
    // bottom-right, handles its own queue internally)
    AchievementToaster.get(this.scene).show({
      icon: achievement.icon,
      name: achievement.name,
      description: achievement.description,
    });
  }

  private loadFromStorage(): string[] {
    try {
      const data = localStorage.getItem('achievements');
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  private saveToStorage(): void {
    try {
      const unlocked = this.achievements.filter(a => a.unlocked).map(a => a.id);
      localStorage.setItem('achievements', JSON.stringify(unlocked));
    } catch {}
  }
}
